import React, { useState, useEffect } from 'react';
import {
  GraduationCap, Clock, Globe, Award, Target, CheckCircle,
  BookOpen, FileText, Users, Mail, Phone, Loader2, ArrowRight, DollarSign, Calendar
} from 'lucide-react';
import { Link } from 'react-router-dom';

const DoctoradoInternacional = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const res = await fetch(`/data/doctorado-internacional.json?v=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) throw new Error('Error al cargar los datos del programa');
        const json = await res.json();
        setData(json);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#0b742f] animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Cargando información del programa...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center px-4">
          <p className="text-red-600 font-semibold text-xl mb-2">Error al cargar la información</p>
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div className="relative bg-gradient-to-r from-[#0b4a8f] to-[#073570] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://recursos.cenid.org/img_cenid/servicios/img_doctorado3.jpg"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl flex-shrink-0">
              <GraduationCap className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2">{data.titulo}</h1>
              <p className="text-xl text-blue-100 mb-1">{data.subtitulo}</p>
              <p className="text-blue-200 flex items-center gap-2 text-sm">
                <Award className="w-4 h-4" />
                {data.rvoe}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">

        {/* ── Quick-info cards ──────────────────────────────────────────── */}
        <div className="grid md:grid-cols-3 gap-6 -mt-12 mb-16 relative z-10">
          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-[#0b4a8f] hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#0b4a8f]/10 p-3 rounded-lg">
                <Clock className="w-6 h-6 text-[#0b4a8f]" />
              </div>
              <h3 className="font-bold text-gray-900">Duración</h3>
            </div>
            <p className="text-2xl font-bold text-[#0b4a8f]">{data.duracion.total}</p>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-[#e87729] hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#e87729]/10 p-3 rounded-lg">
                <Globe className="w-6 h-6 text-[#e87729]" />
              </div>
              <h3 className="font-bold text-gray-900">Modalidad</h3>
            </div>
            <p className="text-xl font-bold text-[#e87729]">{data.subtitulo}</p>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-[#0b742f] hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#0b742f]/10 p-3 rounded-lg">
                <Award className="w-6 h-6 text-[#0b742f]" />
              </div>
              <h3 className="font-bold text-gray-900">RVOE</h3>
            </div>
            <p className="text-lg font-bold text-[#0b742f]">{data.rvoe}</p>
          </div>
        </div>

        {/* ── Presentación ─────────────────────────────────────────────── */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-[#0b4a8f]">
            <div className="flex items-center gap-3 mb-6">
              <BookOpen className="w-8 h-8 text-[#0b4a8f]" />
              <h2 className="text-3xl font-bold text-gray-900">Presentación</h2>
            </div>
            <div className="space-y-4">
              {data.presentacion.map((p, i) => (
                <p key={i} className="text-lg text-gray-700 leading-relaxed">{p}</p>
              ))}
            </div>
          </div>
        </section>

        {/* ── Duración ─────────────────────────────────────────────────── */}
        <section className="mb-16">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-[#0b4a8f] to-[#073570] text-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="w-8 h-8 text-blue-200" />
                <h2 className="text-3xl font-bold">Duración</h2>
              </div>
              <p className="text-4xl font-extrabold mb-2">{data.duracion.total}</p>
              <p className="text-blue-100 text-sm mb-6">Estructura académica organizada en:</p>
              <ul className="space-y-3">
                {data.duracion.estructura.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-blue-50">
                    <CheckCircle className="w-5 h-5 text-blue-300 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              {data.duracion.nota && (
                <p className="mt-6 text-blue-200 text-sm italic border-t border-white/20 pt-4">
                  {data.duracion.nota}
                </p>
              )}
            </div>

            {/* ── Modalidad ──────────────────────────────────────────────── */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <Globe className="w-8 h-8 text-[#e87729]" />
                <h2 className="text-3xl font-bold text-gray-900">Modalidad</h2>
              </div>
              <p className="text-gray-700 mb-5 leading-relaxed">{data.modalidad.descripcion}</p>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">La estructura combina:</p>
              <ul className="space-y-3 mb-5">
                {data.modalidad.estructura.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 bg-orange-50 rounded-lg px-4 py-2">
                    <ArrowRight className="w-4 h-4 text-[#e87729] flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              {data.modalidad.nota && (
                <p className="text-gray-600 text-sm italic border-l-4 border-[#e87729] pl-4">
                  {data.modalidad.nota}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* ── Inversión + Calendario principal ───────────────────────────── */}
        <section className="mb-16">
          <div className="grid md:grid-cols-2 gap-8">
            {data.inversion && (
              <div className="bg-gradient-to-br from-[#e87729] to-[#d66820] text-white rounded-2xl shadow-lg p-8">
                <div className="flex items-center gap-3 mb-6">
                  <DollarSign className="w-8 h-8" />
                  <h3 className="text-2xl font-bold">Inversión</h3>
                </div>
                <div className="space-y-6">
                  <div>
                    <p className="text-orange-100 text-sm mb-1">Inscripción semestral</p>
                    <p className="text-4xl font-bold">{data.inversion.inscripcion}</p>
                  </div>
                  <div>
                    <p className="text-orange-100 text-sm mb-1">Colegiatura mensual</p>
                    <p className="text-4xl font-bold">{data.inversion.mensualidad}</p>
                  </div>
                </div>
              </div>
            )}
            {(data.calendarios && data.calendarios.length > 0) && (
              <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-[#0b742f]">
                <div className="flex items-center gap-3 mb-8">
                  <Calendar className="w-8 h-8 text-[#0b742f]" />
                  <h2 className="text-3xl font-bold text-gray-900">Calendario {data.calendarios[0].periodo}</h2>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-green-50 rounded-lg p-6">
                    <p className="text-sm text-gray-600 mb-2">Periodo de inscripciones</p>
                    <p className="text-lg font-bold text-[#0b742f]">{data.calendarios[0].inscripcion}</p>
                  </div>
                  {data.calendarios[0].resultados && (
                    <div className="bg-orange-50 rounded-lg p-6">
                      <p className="text-sm text-gray-600 mb-2">Publicación de resultados</p>
                      <p className="text-lg font-bold text-[#e87729]">{data.calendarios[0].resultados}</p>
                    </div>
                  )}
                  <div className="bg-green-50 rounded-lg p-6">
                    <p className="text-sm text-gray-600 mb-2">Inicio del ciclo escolar</p>
                    <p className="text-lg font-bold text-[#0b742f]">{data.calendarios[0].inicio}</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-6">
                    <p className="text-sm text-gray-600 mb-2">Fin del periodo escolar</p>
                    <p className="text-lg font-bold text-[#e87729]">{data.calendarios[0].fin}</p>
                  </div>

                </div>
              </div>
            )}
          </div>
        </section>

        {/* ── Calendarios adicionales ───────────────────────────────────── */}
        {(data.calendarios || []).slice(1).map((cal, idx) => (
          <section key={idx} className="mb-16">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-[#0b742f]">
              <div className="flex items-center gap-3 mb-8">
                <Calendar className="w-8 h-8 text-[#0b742f]" />
                <h2 className="text-3xl font-bold text-gray-900">Calendario {cal.periodo}</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 rounded-lg p-6">
                  <p className="text-sm text-gray-600 mb-2">Periodo de inscripciones</p>
                  <p className="text-lg font-bold text-[#0b742f]">{cal.inscripcion}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-6">
                  <p className="text-sm text-gray-600 mb-2">Inicio del ciclo escolar</p>
                  <p className="text-lg font-bold text-[#0b742f]">{cal.inicio}</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-6">
                  <p className="text-sm text-gray-600 mb-2">Fin del periodo escolar</p>
                  <p className="text-lg font-bold text-[#e87729]">{cal.fin}</p>
                </div>
                {cal.resultados && (
                  <div className="bg-orange-50 rounded-lg p-6">
                    <p className="text-sm text-gray-600 mb-2">Publicación de resultados</p>
                    <p className="text-lg font-bold text-[#e87729]">{cal.resultados}</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        ))}

        {/* ── Objetivo General ─────────────────────────────────────────── */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-10 h-10 text-[#e87729]" />
              <h2 className="text-3xl font-bold">Objetivo General</h2>
            </div>
            <p className="text-lg leading-relaxed text-white/95">{data.objetivoGeneral}</p>
          </div>
        </section>

        {/* ── Objetivos Específicos ────────────────────────────────────── */}
        <section className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Objetivos Específicos</h2>
            <div className="h-1 w-24 bg-[#e87729] rounded mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.objetivosEspecificos.map((obj, i) => (
              <div
                key={i}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 hover:border-[#0b4a8f]"
              >
                <div className="bg-[#0b4a8f]/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <span className="text-[#0b4a8f] font-bold text-xl">{i + 1}</span>
                </div>
                <p className="text-gray-700 leading-relaxed">{obj}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Perfil de Egreso ─────────────────────────────────────────── */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#e87729] p-3 rounded-lg">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Perfil de Egreso</h2>
            </div>
            <p className="text-lg text-gray-700 mb-6">{data.perfilEgreso.intro}</p>
            <div className="space-y-4">
              {data.perfilEgreso.items.map((item, i) => (
                <div key={i} className="flex items-start gap-4 bg-green-50 rounded-lg p-4">
                  <CheckCircle className="w-6 h-6 text-[#0b742f] flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Trabajo de Investigación Doctoral ────────────────────────── */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl shadow-xl p-8 md:p-12 border border-indigo-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-indigo-600 p-3 rounded-lg">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Trabajo de Investigación Doctoral</h2>
            </div>
            <p className="text-lg text-gray-700 mb-6">{data.trabajoInvestigacion.descripcion}</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.trabajoInvestigacion.fases.map((fase, i) => (
                <div key={i} className="bg-white rounded-lg shadow-md p-4 border border-indigo-100 flex items-start gap-3">
                  <div className="bg-indigo-600/10 p-2 rounded-lg flex-shrink-0">
                    <span className="text-indigo-600 font-bold text-sm">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <p className="text-gray-700 text-sm font-medium">{fase}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Fundamentación Académica ─────────────────────────────────── */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-[#0b4a8f]">
            <div className="flex items-center gap-3 mb-6">
              <BookOpen className="w-8 h-8 text-[#0b4a8f]" />
              <h2 className="text-3xl font-bold text-gray-900">Fundamentación Académica de la Trayectoria Internacional</h2>
            </div>
            <p className="text-lg text-gray-700 mb-6">{data.fundamentacion.descripcion}</p>
            <ul className="space-y-3 mb-6">
              {data.fundamentacion.items.map((item, i) => (
                <li key={i} className="flex items-start gap-3 bg-blue-50 rounded-lg px-4 py-3">
                  <CheckCircle className="w-5 h-5 text-[#0b4a8f] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            {data.fundamentacion.nota && (
              <p className="text-gray-600 italic border-l-4 border-[#0b4a8f] pl-4 text-sm">
                {data.fundamentacion.nota}
              </p>
            )}
          </div>
        </section>

        {/* ── Reconocimiento Oficial ───────────────────────────────────── */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
            <Award className="w-16 h-16 mx-auto mb-4 text-yellow-300" />
            <h2 className="text-3xl font-bold mb-2">Reconocimiento Oficial</h2>
            <p className="text-green-100 mb-4">{data.reconocimiento.label}</p>
            <p className="text-4xl font-extrabold text-yellow-300 mb-4">{data.reconocimiento.rvoe}</p>
            <p className="text-green-100">{data.reconocimiento.autoridad}</p>
          </div>
        </section>

        {/* ── Dirigido a + Proceso Admisión (2 columnas) ───────────────── */}
        <section className="mb-16">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Dirigido a */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-[#e87729] p-3 rounded-lg">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Dirigido a</h2>
              </div>
              <ul className="space-y-3">
                {data.dirigidoA.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 bg-orange-50 rounded-lg px-4 py-2">
                    <ArrowRight className="w-4 h-4 text-[#e87729] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Proceso de Admisión */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-[#0b4a8f] p-3 rounded-lg">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{data.admision.titulo}</h2>
              </div>
              <ol className="space-y-3">
                {data.admision.pasos.map((paso, i) => (
                  <li key={i} className="flex items-start gap-4 bg-blue-50 rounded-lg px-4 py-3">
                    <span className="bg-[#0b4a8f] text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span className="text-gray-700">{paso}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* ── Documentación ────────────────────────────────────────────── */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl shadow-xl p-8 md:p-12 border border-amber-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-amber-500 p-3 rounded-lg">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">{data.documentacion.titulo}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {data.documentacion.items.map((doc, i) => (
                <div key={i} className="flex items-start gap-3 bg-white rounded-lg shadow-sm p-4">
                  <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{doc}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Contacto Internacional ───────────────────────────────────── */}
        <section className="mb-8">
          <div className="bg-gradient-to-br from-[#0b4a8f] to-[#073570] text-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-4">
              <Globe className="w-8 h-8 text-blue-200" />
              <h2 className="text-3xl font-bold">{data.contacto.titulo}</h2>
            </div>
            <p className="text-blue-100 mb-8 text-lg">{data.contacto.descripcion}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={`mailto:${data.contacto.email}`}
                className="flex items-center gap-3 bg-white/10 hover:bg-white/20 transition-colors rounded-xl px-6 py-4"
              >
                <Mail className="w-6 h-6 text-blue-200 flex-shrink-0" />
                <span className="text-white font-semibold text-lg">{data.contacto.email}</span>
              </a>
              <a
                href={`tel:${data.contacto.telefono.replace(/\s/g, '')}`}
                className="flex items-center gap-3 bg-white/10 hover:bg-white/20 transition-colors rounded-xl px-6 py-4"
              >
                <Phone className="w-6 h-6 text-blue-200 flex-shrink-0" />
                <span className="text-white font-semibold text-lg">{data.contacto.telefono}</span>
              </a>
            </div>
          </div>
        </section>

        {/* ── CTA back ─────────────────────────────────────────────────── */}
        <div className="text-center">
          <Link
            to="/formacion/doctorado"
            className="inline-flex items-center gap-2 text-[#0b4a8f] hover:text-[#073570] font-semibold transition-colors"
          >
            ← Ver Doctorado en Educación (modalidad nacional)
          </Link>
        </div>

      </div>
    </div>
  );
};

export default DoctoradoInternacional;
